// A meeting recording and the signed-in person (BUILD 1 WP-4; guard review UI-1).
//
// When the signed-in person changes (sign-out, a password change, another
// account), the recorder is retired first: the microphone, uploads, preview
// and the recording strip all stop at once, so nothing keeps listening for
// the previous person and the next one sees no strip. Only then is the
// previous person's device spool purged: kept dictation audio goes, but a
// meeting take's unsent audio stays, because deleting it silently would lose
// part of a recording. The same person's next sign-in on this device uploads
// it and offers the meeting back (the reload path). A deleted account keeps
// nothing. Signing out while a recording runs or saves asks first.
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { useNamespaceCopy } from '@/i18n/ns';
import meetingsRecordCopy from '@/i18n/ns/meetings-record';
import type { SpeechSpool } from '@/speech/contracts';
import { speechSpool } from '@/speech/spool';
import { meetingRecorder, type MeetingRecorder } from './meeting-recorder';

/** Phases in which signing out would stop a recording or its upload. */
const IN_PROGRESS = new Set(['starting', 'recording', 'stopping', 'saving']);

/** True while this device records a meeting or still uploads one. */
export function meetingRecordingInProgress(recorder: MeetingRecorder = meetingRecorder): boolean {
  return IN_PROGRESS.has(recorder.store.getState().phase);
}

/**
 * Let go of `owner`'s speech on this device: retire the recorder, then purge
 * the spool. Unsent meeting audio is kept unless the account was deleted.
 */
export async function releaseSignedOutSpeech(
  owner: string,
  {
    accountDeleted = false,
    spool = speechSpool,
    recorder = meetingRecorder,
  }: {
    accountDeleted?: boolean;
    spool?: Pick<SpeechSpool, 'list' | 'remove' | 'purgeOwner'>;
    recorder?: Pick<MeetingRecorder, 'retire'>;
  } = {},
): Promise<void> {
  await recorder.retire();
  if (accountDeleted) return spool.purgeOwner(owner);
  for (const meta of await spool.list(owner, 'dictation')) await spool.remove(meta.id);
}

/** Asked before signing out while a recording runs or saves. */
export function SignOutWhileRecordingDialog({
  open,
  onCancel,
  onSignOut,
}: {
  open: boolean;
  onCancel: () => void;
  onSignOut: () => void;
}) {
  const copy = useNamespaceCopy(meetingsRecordCopy);
  return (
    <Dialog open={open} onOpenChange={(next) => !next && onCancel()}>
      <DialogContent
        showCloseButton={false}
        className="max-w-[calc(100%-2rem)] gap-5 p-5 sm:max-w-sm"
        data-slot="sign-out-while-recording"
      >
        <div className="flex flex-col gap-2">
          <DialogTitle>{copy.signOutRecordingTitle}</DialogTitle>
          <DialogDescription>{copy.signOutRecordingBody}</DialogDescription>
        </div>
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onCancel}
            className="border-border min-h-11 rounded-md border px-4 text-sm font-medium"
          >
            {copy.cancel}
          </button>
          <button
            type="button"
            onClick={onSignOut}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90 min-h-11 rounded-xl px-4 text-sm font-semibold transition-colors duration-200"
          >
            {copy.signOutAnyway}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
